import API_BASE from "./apiConfig";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAppConfig } from "./AppContext";
import { useTheme, getT, glassCard, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { 
  FiUsers, FiUserPlus, FiUserCheck, FiUserX, 
  FiAlertOctagon, FiCheckCircle, FiUserMinus, FiTrendingUp,
  FiCalendar, FiRefreshCw, FiStar, FiEdit3
} from "react-icons/fi";
const BASE = API_BASE;

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  get blue()   { return getBrandColors().blue; },
  get dark()   { return getBrandColors().blueDark; },
  get yellow() { return getBrandColors().yellow; },
  gray:   "rgba(255,255,255,0.45)",
  get green()  { return getBrandColors().green; },
  get red()    { return getBrandColors().red; },
  font:   "'Segoe UI', system-ui, sans-serif",
  pageBg: "linear-gradient(160deg, #020d1a 0%, #040f20 50%, #020c18 100%)",
  card:   { background:"rgba(255,255,255,0.08)", backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)", borderRadius:16, boxShadow:"0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.14)" },
};

const PERIOD_OPTIONS = [
  { key:"month",   label:"This Month" },
  { key:"3months", label:"3 Months"   },
  { key:"6months", label:"6 Months"   },
  { key:"year",    label:"1 Year"     },
  { key:"custom",  label:"Custom"     },
];

const CAT_COLORS = [getBrandColors().yellow, getBrandColors().yellow, getBrandColors().yellow];

// ─── Modern SVG Icons ──────────────────────────────────────────────────────────
function IconStudents({ size=28, color="currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8.5" cy="7" r="3" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M2 20c0-3.31 2.91-6 6.5-6s6.5 2.69 6.5 6" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
      <circle cx="17.5" cy="7.5" r="2.2" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeOpacity="0.7"/>
      <path d="M20.5 20c0-2.48-1.31-4.62-3.3-5.74" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeOpacity="0.7"/>
    </svg>
  );
}

function IconJoined({ size=28, color="currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="9" cy="7.5" r="3" stroke={color} strokeWidth="1.8"/>
      <path d="M2 20c0-3.31 2.91-6 6.5-6 1.42 0 2.74.43 3.82 1.17" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
      <circle cx="18" cy="16" r="4.5" fill={color} fillOpacity="0.12" stroke={color} strokeWidth="1.6"/>
      <path d="M18 13.5v5M15.5 16h5" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}

function IconSuspended({ size=28, color="currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9.5" stroke={color} strokeWidth="1.7"/>
      <rect x="8.5" y="8" width="3" height="8" rx="1.2" fill={color}/>
      <rect x="12.5" y="8" width="3" height="8" rx="1.2" fill={color}/>
    </svg>
  );
}

function IconCancelled({ size=28, color="currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9.5" stroke={color} strokeWidth="1.7"/>
      <path d="M8.5 8.5l7 7M15.5 8.5l-7 7" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function Spin() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <span style={{ display:"inline-block", width:16, height:16, borderRadius:"50%",
      border:"2px solid rgba(255,255,255,0.15)", borderTopColor:T.yellow,
      animation:"exec-spin 0.7s linear infinite", verticalAlign:"middle" }}>
      <style>{`@keyframes exec-spin{to{transform:rotate(360deg)}}`}</style>
    </span>
  );
}

function scoreColor(pct) {
  return T.yellow; // Consistent brand yellow for all score visuals
}

function fmtMonth(mo) {
  if (!mo) return "";
  const [y, m] = mo.split("-");
  return new Date(+y, +m - 1).toLocaleDateString("en-US", { month:"short", year:"2-digit" });
}

function KpiCard({ icon, label, value, sub, accent = "#60a5fa", T, gc }) {
  return (
    <div style={{
      ...gc, padding: "22px 24px",
      display: "flex", alignItems: "center",
      position: "relative", overflow: "hidden", minHeight: 0,
    }}>
      <div style={{
        width: 66, height: 66, borderRadius: 18, flexShrink: 0,
        background: accent + "16", border: `1.5px solid ${accent}28`,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: `0 6px 18px ${accent}12`,
      }}>
        <div style={{ color: accent, fontSize: 32, display: "flex" }}>{icon}</div>
      </div>
      
      {/* Vertical Gray Line with Gap */}
      <div style={{
        width: 1.5, height: 42,
        background: "rgba(150,150,165,0.22)",
        margin: "0 24px", flexShrink: 0,
      }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 9.5, fontWeight: 800, letterSpacing: "0.12em",
          textTransform: "uppercase", color: T.textMuted || "rgba(150,160,170,0.8)", marginBottom: 5,
          lineHeight: 1.3,
        }}>{label}</div>
        <div style={{
          fontSize: 30, lineHeight: 1.05, fontWeight: 900,
          letterSpacing: "-0.04em", color: T.textPrimary || "#000",
          fontFamily: "'SF Pro Display','Segoe UI',system-ui,sans-serif",
        }}>{value}</div>
        {sub && <div style={{ fontSize: 10.5, color: T.textMuted || "rgba(150,160,170,0.8)", fontWeight: 600, marginTop: 4, display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: accent }} />
          {sub}
        </div>}
      </div>
    </div>
  );
}

// ─── ResizeObserver hook — measures container pixel width ────────────────────
function useContainerWidth(ref, fallback = 600) {
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    if (!ref.current) return;
    setWidth(ref.current.offsetWidth || fallback);
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref, fallback]);
  return width;
}

// ─── ResizeObserver hook — measures both width & height ──────────────────────
function useContainerSize(ref, fallbackW = 600, fallbackH = 220) {
  const [size, setSize] = useState({ w: fallbackW, h: fallbackH });
  useEffect(() => {
    if (!ref.current) return;
    setSize({ w: ref.current.offsetWidth || fallbackW, h: ref.current.offsetHeight || fallbackH });
    const ro = new ResizeObserver(([e]) => setSize({
      w: e.contentRect.width,
      h: Math.max(e.contentRect.height, 80),
    }));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref, fallbackW, fallbackH]); // eslint-disable-line react-hooks/exhaustive-deps
  return size;
}

// ─── SVG Enrollment Chart ─────────────────────────────────────────────────────
function EnrollmentChart({ data }) {
  const { isDark }   = useTheme();
  const T            = getT(isDark);
  const containerRef = useRef(null);
  const containerW   = useContainerWidth(containerRef, 560);

  if (!data || data.length === 0) return <div style={{ color:T.gray, padding:24, textAlign:"center" }}>No data</div>;

  const bars    = [
    { key:"joined",    color:T.green,  label:"Joined"    },
    { key:"suspended", color:T.orange, label:"Suspended" },
    { key:"cancelled", color:T.red,    label:"Cancelled" },
  ];
  const maxVal  = Math.max(1, ...data.flatMap(d => [d.joined, d.suspended, d.cancelled]));
  const topPad  = 18;   // headroom above tallest bar for value labels
  const chartH  = 120;
  const labelH  = 22;
  const leftPad = 28;
  const totalH  = topPad + chartH + labelH;
  const n       = data.length;

  // Distribute available width evenly — bars fill full card width
  const availW  = Math.max(containerW - leftPad - 8, n * 50);
  const groupW  = Math.floor(availW / n);
  const barGap  = Math.max(2, Math.floor(groupW * 0.06));
  const barW    = Math.max(8, Math.floor((groupW - barGap * 2 - groupW * 0.25) / 3));
  const totalW  = containerW;

  return (
    <div ref={containerRef}>
      {/* Legend */}
      <div style={{ display:"flex", gap:18, marginBottom:10, flexWrap:"wrap" }}>
        {bars.map(b => (
          <div key={b.key} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, fontWeight:700, color:T.gray }}>
            <div style={{ width:10, height:10, borderRadius:3, background:b.color }}/>
            {b.label}
          </div>
        ))}
      </div>

      <svg width={totalW} height={totalH} style={{ fontFamily:T.font, display:"block" }}>
        {/* Grid lines with Y-axis labels */}
        {[0, 25, 50, 75, 100].map(pct => {
          const y   = topPad + chartH - (pct / 100) * chartH;
          const val = Math.round(maxVal * pct / 100);
          return (
            <g key={pct}>
              {pct > 0 && val > 0 && (
                <text x={leftPad - 4} y={y + 3} fontSize={9} fill={T.gray} textAnchor="end">{val}</text>
              )}
              <line x1={leftPad} y1={y} x2={totalW} y2={y}
                stroke={pct === 0 ? T.gray : T.divider}
                strokeWidth={pct === 0 ? 1.5 : 1}
                strokeDasharray={pct > 0 && pct < 100 ? "4,3" : ""}/>
            </g>
          );
        })}

        {/* Bar groups */}
        {data.map((d, gi) => {
          const gx       = leftPad + gi * groupW + Math.floor(groupW * 0.12);
          const groupMid = barW * 1.5 + barGap;
          return (
            <g key={d.month}>
              {bars.map((b, bi) => {
                const val  = d[b.key] || 0;
                const bh   = val > 0 ? Math.max((val / maxVal) * chartH, 4) : 0;
                const bx   = gx + bi * (barW + barGap);
                const by   = topPad + chartH - bh;
                // Clamp label: if bar reaches near top, push label down inside bar
                const lblY = by < topPad + 4 ? by + 12 : by - 4;
                const lblFill = by < topPad + 4 ? "#fff" : b.color;
                return (
                  <g key={b.key}>
                    {val > 0 && (
                      <>
                        <rect x={bx} y={by} width={barW} height={bh}
                          fill={b.color} fillOpacity={0.88} rx={3}/>
                        <text x={bx + barW / 2} y={lblY}
                          textAnchor="middle" fontSize={9} fill={lblFill} fontWeight="bold">{val}</text>
                      </>
                    )}
                  </g>
                );
              })}
              {/* Month label */}
              <text x={gx + groupMid} y={topPad + chartH + 16}
                textAnchor="middle" fontSize={10} fill={T.gray} fontWeight="600">
                {fmtMonth(d.month)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── SVG Performance Trend Chart — fills flex container height ───────────────
function PerfTrendChart({ data }) {
  const { isDark }   = useTheme();
  const T            = getT(isDark);
  const containerRef = useRef(null);
  const { w: containerW, h: containerH } = useContainerSize(containerRef, 480, 220);

  if (!data || data.length === 0) return (
    <div ref={containerRef} style={{ flex:1, minHeight:80 }}/>
  );
  const valid = data.filter(d => d.avg_pct != null);
  if (valid.length === 0) return (
    <div ref={containerRef} style={{ flex:1, minHeight:80, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <span style={{ color:T.gray, fontSize:13 }}>No performance data in this period</span>
    </div>
  );

  const topPad  = 20;   // room above tallest bar for value labels
  const labelH  = 22;   // month label area below bars
  const chartH  = Math.max(containerH - topPad - labelH - 2, 60);
  const totalH  = topPad + chartH + labelH;
  const leftPad = 34;
  const n       = valid.length;

  // Bars fill the full container width evenly
  const availW  = Math.max(containerW - leftPad - 8, n * 40);
  const slot    = Math.floor(availW / n);
  const barW    = Math.max(16, Math.floor(slot * 0.62));
  const gap     = slot - barW;
  const totalW  = containerW;

  return (
    <div ref={containerRef} style={{ flex:1, minHeight:80, overflow:"hidden" }}>
      <svg width={totalW} height={totalH} style={{ fontFamily:T.font, display:"block" }}>
        {/* Horizontal grid lines with Y-axis labels */}
        {[25, 50, 75, 100].map(pct => {
          const y = topPad + chartH - (pct / 100) * chartH;
          return (
            <g key={pct}>
              <text x={leftPad - 4} y={y + 3} fontSize={9} fill={T.gray} textAnchor="end">{pct}%</text>
              <line x1={leftPad} y1={y} x2={totalW} y2={y}
                stroke={T.divider} strokeWidth={1} strokeDasharray="4,3"/>
            </g>
          );
        })}

        {/* Bars */}
        {valid.map((d, i) => {
          const bh    = Math.max((d.avg_pct / 100) * chartH, 4);
          const bx    = leftPad + gap / 2 + i * slot;
          const by    = topPad + chartH - bh;
          const color = scoreColor(d.avg_pct);
          // Clamp label: always stays inside topPad zone, never overlaps grid label
          const lblY  = Math.max(by - 5, topPad + 10);
          return (
            <g key={d.month}>
              <rect x={bx} y={by} width={barW} height={bh} fill={color} fillOpacity={0.85} rx={4}/>
              <text x={bx + barW / 2} y={lblY}
                textAnchor="middle" fontSize={10} fill={color} fontWeight="bold">{d.avg_pct}%</text>
              <text x={bx + barW / 2} y={topPad + chartH + 17}
                textAnchor="middle" fontSize={10} fill={T.gray} fontWeight="600">{fmtMonth(d.month)}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Horizontal bar ───────────────────────────────────────────────────────────
function HBar({ pct, color }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div style={{ flex:1, height:7, borderRadius:4, background:"rgba(255,255,255,0.10)", overflow:"hidden" }}>
      <div style={{ width:`${Math.min(pct,100)}%`, height:"100%", borderRadius:4,
        background:color, transition:"width 0.5s ease" }}/>
    </div>
  );
}

// ─── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div style={{ 
      fontSize: 16, fontWeight: 900, color: T.blue, textTransform: "uppercase",
      letterSpacing: "0.15em", marginBottom: 18, marginTop: 14, 
      display: "flex", alignItems: "center", gap: 12 
    }}>
      <div style={{ width: 5, height: 20, borderRadius: 2, background: T.yellow, boxShadow: `0 0 12px ${T.yellow}aa` }}/>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function ExecutiveDashboard() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { config } = useAppConfig();
  const orgName = config.org_name || "Sixers Cricket Academy";

  const [period,    setPeriod]    = useState("3months");
  const [startDate, setStartDate] = useState("");
  const [endDate,   setEndDate]   = useState("");
  const [intelData,   setIntelData]   = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [data,      setData]      = useState(null);
  const [coachEvals, setCoachEvals] = useState([]);
  const [evalStats, setEvalStats] = useState({ total_evaluations: 0, total_data_points: 0, avg_overall_score: 0 });
  const [evalHistory, setEvalHistory] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [intelOpen, setIntelOpen] = useState({});

  const loadIntelligence = useCallback(async () => {
    setIntelLoading(true);
    try {
      const r = await fetch(`${BASE}/programs/reports/coach-effectiveness`);
      const d = await r.json();
      if (r.ok) setIntelData(d);
    } catch (e) { console.error("Intel fetch failed", e); }
    finally { setIntelLoading(false); }
  }, []);

  const loadCoachEvaluations = useCallback(async () => {
    try {
      const [revRes, statRes] = await Promise.all([
        fetch(`${BASE}/evaluations/summary-all`),
        fetch(`${BASE}/evaluations/total-stats`)
      ]);
      const [revData, statData] = await Promise.all([revRes.json(), statRes.json()]);
      
      if (revRes.ok) setCoachEvals(Array.isArray(revData) ? revData : []);
      if (statRes.ok) setEvalStats(statData);
    } catch (e) {
      console.error("Coach evals fetch failed", e);
    }
  }, []);

  const loadEvalHistory = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: 15 });
      if (data?.date_from) params.append("start_date", data.date_from);
      if (data?.date_to)   params.append("end_date",   data.date_to);

      const r = await fetch(`${BASE}/evaluations/history?${params}`);
      const d = await r.json();
      if (r.ok && d.success) setEvalHistory(d.data);
    } catch (e) { console.error("Feedback history fetch failed", e); }
  }, [data]);

  const loadDashboard = useCallback(async () => {
    setLoading(true); setError(""); setData(null);
    const p = new URLSearchParams({ period });
    if (period === "custom") {
      if (!startDate || !endDate) { setLoading(false); setError("Please select start and end dates."); return; }
      p.set("start_date", startDate);
      p.set("end_date",   endDate);
    }
    try {
      const r = await fetch(`${BASE}/executive/dashboard?${p}`);
      const d = await r.json();
      if (!r.ok) { setError(d.detail || "Failed to load dashboard"); return; }
      setData(d);
      setIntelOpen({});
    } catch { setError("Unable to reach the server."); }
    finally { setLoading(false); }
  }, [period, startDate, endDate]);

  useEffect(() => {
    if (period !== "custom") loadDashboard();
    loadIntelligence();
    loadCoachEvaluations();
  }, [period, loadDashboard, loadIntelligence, loadCoachEvaluations]);

  useEffect(() => {
    if (data) loadEvalHistory();
  }, [data, loadEvalHistory]);

  const toggleIntel = (sid) => setIntelOpen(o => ({ ...o, [sid]: !o[sid] }));
  const gc = glassCard(T);

  // ── KPI card definition ─────────────────────────────────────────────────────
  const kpiCards = data ? [
    { Icon:FiUsers,     label:"Active Students",   val:data.summary.total_active,    accent:"#60a5fa" },
    { Icon:FiUserPlus,  label:"Joined",            val:data.summary.total_joined,    accent:getBrandColors().green },
    { Icon:FiUserMinus, label:"Suspended",         val:data.summary.total_suspended, accent:getBrandColors().orange },
    { Icon:FiUserX,     label:"Cancelled",         val:data.summary.total_cancelled, accent:getBrandColors().red },
  ] : [];

  return (
    <div style={{ minHeight:"100%", background:T.pageBg, fontFamily:T.font }}>

      {/* ── Transparent header matching StudentMaster layout ──────────────────── */}
      <div style={{ padding:"24px 24px 10px" }}>
        
        {/* Title block */}
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:20, justifyContent:"space-between", flexWrap: "wrap" }}>
          
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <PageHeaderIcon icon={<FiCalendar size={20} color={getBrandColors().blueDark} />} />
            <div>
              <h2 style={{ fontSize: "22px", fontWeight: 800, color: T.textPrimary, margin: 0, letterSpacing: "-0.01em" }}>
                Performance Intelligence Dashboard
              </h2>
              <p style={{ margin: 0, fontSize: "12px", color: T.textMuted, marginTop: "1px", lineHeight: 1.5 }}>
                {data ? `${data.date_from} → ${data.date_to}` : "Coach performance & student intelligence"}
              </p>
            </div>
          </div>

          {loading && !data && (
            <div style={{ color:T.textMuted, fontSize:13, display:"flex", alignItems:"center", gap:8 }}>
              <Spin/> Loading…
            </div>
          )}
        </div>

        {/* Period selector row — flush bottom of header */}
        {/* Period selector row */}
        <div style={{ display:"flex", gap:4, alignItems:"center", flexWrap:"wrap", paddingBottom: 10 }}>
          {PERIOD_OPTIONS.map(opt => (
            <button key={opt.key} onClick={() => setPeriod(opt.key)}
              style={{
                padding:"8px 20px", borderRadius:20, border:"none", cursor:"pointer",
                fontSize:12, fontFamily:T.font, transition:"all 0.15s",
                background: period === opt.key ? T.blue : (T.isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"),
                color:       period === opt.key ? "#fff" : T.textMuted,
                fontWeight:  period === opt.key ? 800 : 700,
              }}>{opt.label}</button>
          ))}
          {period === "custom" && (
            <>
              <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)}
                style={{ padding:"5px 10px", borderRadius:7,
                  border: isDark ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(3,78,123,0.25)",
                  fontSize:12, color: isDark ? "#fff" : getBrandColors().blueDark, fontFamily:T.font, outline:"none",
                  background: isDark ? "rgba(255,255,255,0.12)" : "rgba(3,78,123,0.07)", marginLeft:4,
                  colorScheme: isDark ? "dark" : "light" }}/>
              <span style={{ color: isDark ? "rgba(255,255,255,0.5)" : "rgba(3,78,123,0.5)", fontSize:12 }}>→</span>
              <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)}
                style={{ padding:"5px 10px", borderRadius:7,
                  border: isDark ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(3,78,123,0.25)",
                  fontSize:12, color: isDark ? "#fff" : getBrandColors().blueDark, fontFamily:T.font, outline:"none",
                  background: isDark ? "rgba(255,255,255,0.12)" : "rgba(3,78,123,0.07)",
                  colorScheme: isDark ? "dark" : "light" }}/>
              <button onClick={loadDashboard}
                style={{ padding:"5px 14px", borderRadius:7, border:"none", cursor:"pointer",
                  fontSize:12, fontWeight:800, fontFamily:T.font, marginLeft: 4,
                  background:T.blue, color:"#fff" }}>
                Apply
              </button>
            </>
          )}
          <button onClick={loadDashboard} disabled={loading}
            style={{ marginLeft:"auto", padding:"6px 14px", borderRadius:20,
              cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:T.font,
              background: T.isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.08)", 
              color: T.blue, border: `1.5px solid ${T.isDark ? "rgba(255,255,255,0.2)" : "rgba(3,78,123,0.3)"}`,
              display:"flex", alignItems:"center", gap:5 }}>
            {loading ? <Spin/> : <>
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none">
                <path d="M20.5 12A8.5 8.5 0 1112 3.5c2.33 0 4.44.94 5.97 2.47L21 3v7h-7l2.47-2.47A6.5 6.5 0 1018.5 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
              Refresh
            </>}
          </button>
        </div>
      </div>

      {/* ── Content area ────────────────────────────────────────────────────── */}
      <div style={{ padding:"0 24px 48px" }}>

        {error && <div style={{ padding:"10px 16px", borderRadius:10, background:"rgba(248,113,113,0.12)", backdropFilter:"blur(20px)", WebkitBackdropFilter: "blur(20px)",
          color:getBrandColors().red, fontWeight:600, marginBottom:12, border:"1px solid rgba(248,113,113,0.30)", fontSize:13 }}>{error}</div>}

        {!loading && !data && !error && (
          <div style={{ textAlign:"center", padding:64, color:T.gray }}>
            <svg width={52} height={52} viewBox="0 0 24 24" fill="none" style={{ margin:"0 auto 12px", display:"block", opacity:0.3 }}>
              <rect x="2" y="12" width="4" height="10" rx="1.5" fill={T.textPrimary}/>
              <rect x="10" y="7" width="4" height="15" rx="1.5" fill={T.textPrimary}/>
              <rect x="18" y="3" width="4" height="19" rx="1.5" fill={T.textPrimary}/>
            </svg>
            <div style={{ fontSize:14, fontWeight:700 }}>Select a period to load the dashboard.</div>
          </div>
        )}

        {!loading && data && (
          <>
            {/* ══ STUDENT METRICS (Moved down from header) ══════════════════════ */}
            <SectionLabel>Student Metrics</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16, marginBottom: 26 }}>
              {kpiCards.map(card => (
                <KpiCard
                  key={card.label}
                  icon={<card.Icon />}
                  label={card.label}
                  value={card.val}
                  accent={card.accent}
                  T={T} gc={gc}
                />
              ))}
            </div>
            {/* ══ INTELLIGENCE SCORECARD (COACH PERFORMANCE) ══════════════════════ */}
            {intelData && intelData.summary && (
              <>
                <SectionLabel>Coach Performance Intelligence</SectionLabel>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16, marginBottom: 22 }}>
                  {[
                    {
                      icon: <FiAlertOctagon />, label: "Action Required",
                      val: intelData.summary.high_alerts,
                      accent: getBrandColors().red,
                      sub: "Critical"
                    },
                    {
                      icon: <FiCheckCircle />, label: "Compliance Rate",
                      val: `${intelData.summary.avg_compliance_pct}%`,
                      accent: getBrandColors().green,
                    },
                    {
                      icon: <FiUserMinus />, label: "Students at Risk",
                      val: intelData.summary.students_low_attendance,
                      accent: getBrandColors().orange,
                    },
                    {
                      icon: <FiTrendingUp />, label: "Total Points",
                      val: evalStats.total_data_points.toLocaleString(),
                      accent: "#60a5fa",
                    },
                    {
                      icon: <FiStar />, label: "Overall Score",
                      val: evalStats.avg_overall_score.toFixed(1),
                      accent: "#a78bfa",
                    },
                    {
                      icon: <FiEdit3 />, label: "Reviews",
                      val: evalStats.total_evaluations,
                      accent: "#22d3ee",
                    }
                  ].map(card => (
                    <KpiCard
                      key={card.label}
                      icon={card.icon}
                      label={card.label}
                      value={card.val}
                      sub={card.sub}
                      accent={card.accent}
                      T={T}
                      gc={gc}
                    />
                  ))}
                </div>

                {/* ── Action Items ── */}
                {intelData.alerts && intelData.alerts.length > 0 && (
                  <div style={{ ...gc, padding: "16px 20px", marginBottom: 18 }}>
                    <div style={{ fontSize: 13, fontWeight: 900, color: T.textPrimary, marginBottom: 12 }}><span style={{ fontSize: 20 }}>🚨</span> Critical Coach Alerts</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {intelData.alerts.slice(0, 5).map((alert, i) => (
                        <div key={i} style={{
                          display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                          borderRadius: 10,
                          border: `1px solid ${alert.severity === "high" ? "rgba(248,113,113,0.30)" : "rgba(251,191,36,0.30)"}`,
                          background: alert.severity === "high" ? "rgba(248,113,113,0.10)" : "rgba(251,191,36,0.08)",
                        }}>
                          <span style={{
                            padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 900, whiteSpace: "nowrap",
                            background: alert.severity === "high" ? T.red : T.orange,
                            color: "#fff", textTransform: "uppercase", letterSpacing: "0.07em",
                          }}>{alert.severity === "high" ? "HIGH" : "MED"}</span>
                          <span style={{ flex: 1, fontSize: 13, color: T.textPrimary, fontWeight: 600 }}>{alert.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* ── Batch Compliance (Coach Effectiveness) ── */}
                {intelData.compliance && intelData.compliance.length > 0 && (
                  <div style={{ ...gc, padding: "20px 24px", marginBottom: 20 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                      <FiCalendar size={22} color={T.textPrimary} />
                      <div style={{ fontSize: 15, fontWeight: 900, color: T.textPrimary }}>Coach Attendance Compliance</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
                      {intelData.compliance.slice(0, 6).map(comp => (
                        <div key={comp.batch_id} style={{
                          padding: "20px", borderRadius: 14, background: "rgba(255,255,255,0.03)",
                          border: `1.5px solid ${T.yellow}33`,
                          boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                            <span style={{ fontSize: 15, fontWeight: 900, color: T.textPrimary }}>{comp.batch_name}</span>
                            <span style={{ fontSize: 20, fontWeight: 900, color: T.yellow }}>
                              {comp.compliance_pct}%
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                            <HBar pct={comp.compliance_pct} color={T.yellow} />
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: T.blue, fontWeight: 900 }}>
                            <span>{comp.logged_days} / {comp.expected_days} sessions logged</span>
                            <span style={{ textTransform: "uppercase", fontSize: 12, letterSpacing: "0.05em", color: T.blue }}>{comp.compliance_pct >= 90 ? "Excellent" : "Needs Review"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* ══ COACH PERFORMANCE INTELLIGENCE ══════════════════════════════════════ */}
                {coachEvals.length > 0 && (
                  <div style={{ ...gc, padding: "24px", marginBottom: 24, background: "rgba(255,255,255,0.08)", border: `1px solid ${T.yellow}44` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:22 }}>
                      <FiStar size={24} color={T.yellow} fill={T.yellow} />
                      <div style={{ fontSize: 18, fontWeight: 900, color: T.blue }}>Coach performance detailed scoring</div>
                    </div>
                    
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))", gap: 20 }}>
                      {coachEvals.map(evalItem => (
                        <div key={evalItem.id} style={{
                          padding: "24px", borderRadius: 16, background: "rgba(255,255,255,0.30)",
                          border: `1.5px solid ${T.yellow}33`,
                          boxShadow: `0 8px 16px rgba(0,0,0,0.05)`,
                          display: "flex", flexDirection: "column", gap: 16
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <div style={{ fontSize: 18, fontWeight: 900, color: T.blue }}>{evalItem.name}</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(3,78,123,0.5)", textTransform: "uppercase", marginTop: 2 }}>{evalItem.review_count} Reviews</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: 28, fontWeight: 900, color: T.yellow }}>{evalItem.avg_score ? parseFloat(evalItem.avg_score).toFixed(1) : "0"}%</div>
                              <div style={{ fontSize: 10, fontWeight: 800, color: T.blue, textTransform: "uppercase" }}>Overall Score</div>
                            </div>
                          </div>

                          <div style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
                            {evalItem.categories.map(cat => (
                              <div key={cat.category} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.6)", textTransform: "uppercase" }}>{cat.category}</span>
                                  <span style={{ fontSize: 12, fontWeight: 900, color: "#fff" }}>{parseFloat(cat.score).toFixed(1)}/5</span>
                                </div>
                                <div style={{ height: 4, background: "rgba(255,255,255,0.05)", borderRadius: 2 }}>
                                  <div style={{ height: "100%", width: `${(cat.score / 5) * 100}%`, background: T.yellow, borderRadius: 2 }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ══ PERFORMANCE ALERTS & RECENT FEEDBACK ══════════════════════════════════════ */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: 20, marginBottom: 40 }}>
                  
                  {/* Left: Intelligence Analytics (Alerts) */}
                  <div style={{ ...gc, padding: "24px", minHeight: 400 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:22 }}>
                      <FiAlertOctagon size={24} color={T.red} />
                      <div style={{ fontSize: 18, fontWeight: 900, color: T.textPrimary }}>Performance alerts</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {coachEvals.filter(ce => ce.avg_score < 75).length === 0 ? (
                        <div style={{ padding: "20px", borderRadius: 12, background: "rgba(16,185,129,0.06)", border: "1px dashed rgba(16,185,129,0.3)" }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: "#10b981", textAlign: "center" }}>All coaches performing optimally.</div>
                        </div>
                      ) : (
                        coachEvals.filter(ce => ce.avg_score < 75).map(coach => (
                          <div key={coach.id} style={{
                            padding: "18px 24px", borderRadius: 16, background: "rgba(240,68,68,0.06)",
                            border: "1.5px solid rgba(240,68,68,0.15)", display: "flex", alignItems: "center", gap: 16
                          }}>
                            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(240,68,68,0.15)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                              <FiTrendingUp style={{ transform: "rotate(90deg)" }} color={T.red} size={20} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 16, fontWeight: 900, color: "#fff" }}>{coach.name}</div>
                              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", fontWeight: 700 }}>Scoring below 75% on technical KPIs</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: 20, fontWeight: 900, color: T.red }}>{parseFloat(coach.avg_score).toFixed(1)}%</div>
                              <div style={{ fontSize: 10, fontWeight: 800, color: T.red, textTransform: "uppercase" }}>Critical</div>
                            </div>
                          </div>
                        ))
                      )}
                      
                      <div style={{ marginTop: 10, padding: 20, borderRadius: 14, background: "rgba(3,78,123,0.08)", border: `1.5px solid ${T.yellow}66` }}>
                        <div style={{ fontSize: 13, fontWeight: 900, color: T.blue, textTransform: "uppercase", marginBottom: 8, letterSpacing: "0.05em" }}>AI Intelligence Insights</div>
                        <div style={{ fontSize: 14, color: T.blue, lineHeight: 1.6, fontWeight: 700 }}>
                          {coachEvals.length > 0 ? (
                            `Based on ${evalHistory.length} recent evaluations, your academy is performing with ${evalStats.avg_overall_score.toFixed(1)}% excellence. Recommend continuous feedback cycles.`
                          ) : "Insufficient evaluation data for AI trend analysis."}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Feedback Feed */}
                  <div style={{ ...gc, padding: "24px", minHeight: 400 }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent: "space-between", marginBottom:22 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <FiCheckCircle size={24} color={T.teal} />
                        <div style={{ fontSize: 18, fontWeight: 900, color: T.textPrimary }}>Recent feedback feed</div>
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.45)", textTransform: "uppercase" }}>Last {evalHistory.length} logs</div>
                    </div>
                    
                    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxHeight: 400, overflowY: "auto", paddingRight: 10 }}>
                      {evalHistory.map(feed => (
                        <div key={feed.id} style={{
                          padding: "16px 20px", borderRadius: 14, background: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center"
                        }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: T.blue }}>{feed.coach_name}</div>
                          <div style={{ fontSize: 18, fontWeight: 900, color: T.yellow }}>{parseFloat(feed.overall_score).toFixed(1)}%</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ══ ENROLLMENT TREND CHART ══════════════════════════════════════ */}
            <SectionLabel>Enrollment Movement</SectionLabel>
            <div style={{ ...gc, padding:"16px 20px", marginBottom:14 }}>
              <EnrollmentChart data={data.enrollment_trend}/>
            </div>

            {/* ══ PERFORMANCE SECTION ════════════════════════════════════════ */}
            <SectionLabel>Performance Overview</SectionLabel>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14, alignItems:"stretch" }}>

              {/* Performance trend — flex-column so chart fills remaining height */}
              <div style={{ ...gc, padding:"16px 20px", display:"flex", flexDirection:"column" }}>
                <div style={{ fontSize:13, fontWeight:800, color:T.textPrimary, marginBottom:10, flexShrink:0 }}>Monthly Avg Score %</div>
                <PerfTrendChart data={data.performance_trend} />
              </div>

              {/* Category performance */}
              <div style={{ ...gc, padding:"16px 20px" }}>
                <div style={{ fontSize:13, fontWeight:800, color:T.textPrimary, marginBottom:12 }}>Score by Category</div>
                {data.category_performance.length === 0 ? (
                  <div style={{ color:T.gray, fontSize:13, textAlign:"center", padding:24 }}>No data in this period</div>
                ) : (
                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {data.category_performance.map((cat, i) => (
                      <div key={cat.category_id}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                          <span style={{ fontSize:12, fontWeight:700, color:T.blue }}>{cat.category_name}</span>
                          <span style={{ fontSize:12, fontWeight:900, color:scoreColor(cat.pct) }}>{cat.pct}%</span>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <HBar pct={cat.pct} color={CAT_COLORS[i % CAT_COLORS.length]}/>
                        </div>
                        <div style={{ fontSize:9, color:T.gray, marginTop:2 }}>
                          Avg {cat.avg_score}/{cat.max_score} · {cat.indicator_count} indicators · {cat.student_count} students
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ══ EXECUTIVE INTELLIGENCE ══════════════════════════════════════ */}
            <SectionLabel>Executive Intelligence — Critical Student Alerts</SectionLabel>

            {data.students_needing_attention.length === 0 ? (
              <div style={{ ...gc, padding:"32px", textAlign:"center", background:"rgba(52,211,153,0.08)", border:`2px solid rgba(52,211,153,0.2)` }}>
                <FiCheckCircle size={48} color={T.green} style={{ margin:"0 auto 12px", display:"block", opacity:0.8 }} />
                <div style={{ fontSize:16, fontWeight:800, color:T.green }}>All students are performing within optimal thresholds.</div>
              </div>
            ) : (
              <div style={{ ...gc, padding:"20px 24px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
                  <FiAlertOctagon size={24} color={T.yellow} strokeWidth={2} />
                  <div style={{ fontSize:15, fontWeight:900, color:T.textPrimary }}>Students Requiring 1-on-1 Training</div>
                  <span style={{ padding:"4px 14px", borderRadius:20, fontSize:13, fontWeight:900,
                    background:"rgba(255,255,255,0.12)", color:T.yellow, border:`1px solid ${T.yellow}55` }}>
                    {data.students_needing_attention.length} Critical
                  </span>
                </div>

                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {data.students_needing_attention.map(stu => {
                    const isOpen = intelOpen[stu.student_id];
                    const worst  = stu.weak_indicators[0];
                    return (
                      <div key={stu.student_id} style={{ borderRadius:10,
                        border:T.glassBdr, overflow:"hidden",
                        background: isOpen ? T.hoverRowBg : T.rowOdd }}>
                        <div onClick={() => toggleIntel(stu.student_id)}
                          style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 16px",
                            cursor:"pointer", borderBottom: isOpen ? T.glassBdr : "none" }}>
                          {/* Avatar */}
                          <div style={{ width:38, height:38, borderRadius:"50%", flexShrink:0,
                            background:`linear-gradient(135deg,${T.blue},#0369a1)`,
                            display:"flex", alignItems:"center", justifyContent:"center",
                            fontSize:13, fontWeight:900, color:"#fff" }}>
                            {stu.student_name.split(" ").map(n=>n[0]).join("").slice(0,2)}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:800, color:T.textPrimary }}>{stu.student_name}</div>
                            <div style={{ fontSize:10, color:T.gray, marginTop:1 }}>
                              {stu.weak_indicators.length} weak area{stu.weak_indicators.length!==1?"s":""}&nbsp;·&nbsp;
                              Worst: <span style={{ color:T.red, fontWeight:700 }}>{worst?.indicator_name} ({worst?.pct}%)</span>
                            </div>
                          </div>
                          {/* Score pills */}
                          <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                            {stu.weak_indicators.slice(0,3).map((wi,i) => (
                              <div key={i} title={`${wi.indicator_name} — ${wi.pct}%`}
                                style={{ padding:"3px 8px", borderRadius:6, fontSize:10, fontWeight:900, color:"#fff",
                                  background:scoreColor(wi.pct) }}>
                                {wi.pct}%
                              </div>
                            ))}
                            {stu.weak_indicators.length > 3 && (
                              <div style={{ padding:"3px 8px", borderRadius:6, fontSize:10, fontWeight:900,
                                background:T.btnGlassBg, color:T.btnGlassClr }}>
                                +{stu.weak_indicators.length - 3}
                              </div>
                            )}
                            <span style={{ fontSize:13, color:T.gray, marginLeft:4 }}>{isOpen ? "▲" : "▼"}</span>
                          </div>
                        </div>

                        {isOpen && (
                          <div style={{ padding:"12px 16px" }}>
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:8 }}>
                              {stu.weak_indicators.map((wi, i) => (
                                <div key={i} style={{ padding:"10px 12px", borderRadius:8,
                                  background: wi.pct < 30 ? "rgba(248,113,113,0.12)" : "rgba(251,191,36,0.08)",
                                  border:`1px solid ${scoreColor(wi.pct)}44` }}>
                                  <div style={{ fontSize:11, fontWeight:800, color:T.textPrimary, marginBottom:1 }}>{wi.indicator_name}</div>
                                  <div style={{ fontSize:9, color:T.gray, marginBottom:7 }}>{wi.category_name}</div>
                                  <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:5 }}>
                                    <HBar pct={wi.pct} color={scoreColor(wi.pct)}/>
                                    <span style={{ fontSize:10, fontWeight:900, color:scoreColor(wi.pct), whiteSpace:"nowrap" }}>
                                      {wi.avg_score}/{wi.max_score}
                                    </span>
                                  </div>
                                  <span style={{ padding:"2px 8px", borderRadius:20, display:"inline-block",
                                    fontSize:8, fontWeight:800, background:scoreColor(wi.pct), color:"#fff" }}>
                                    {wi.pct < 30 ? "Critical" : "Below Threshold"} ({wi.pct}%)
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
